// src/components/roster/ChannelBadges.jsx
//
// Three compact channel badges showing per-parent reachability:
//   📱 push   — green if their user-doc has an fcmToken (Scrimly app
//               installed + signed in at some point); gray otherwise
//   💬 SMS    — green if user-doc.smsOptIn === true AND valid phone;
//               amber if has phone but not opted in; gray if no phone
//   📧 email  — green if a parseable email is on the player record;
//               gray otherwise
//
// Each badge has a title attribute so hovering reveals the precise
// state. Designed to fit inline beneath the parent name/phone column
// of the roster row without taking a new line on desktop.

import React from 'react';

const STATE_CLASSES = {
  on:      'bg-lime-100 text-lime-800 border-lime-200',
  partial: 'bg-amber-100 text-amber-800 border-amber-200',
  off:     'bg-zinc-100 text-zinc-400 border-zinc-200',
  unknown: 'bg-zinc-100 text-zinc-400 border-zinc-200 border-dashed',
};

function Badge({ icon, label, state, title }) {
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${STATE_CLASSES[state]}`}
    >
      <span aria-hidden>{icon}</span>
      <span className="hidden sm:inline">{label}</span>
    </span>
  );
}

export default function ChannelBadges({
  hasPush,        // boolean — fcmToken present on parent's user-doc
  pushUnknown,    // boolean — no user-doc resolved (parent never signed in)
  hasSms,         // boolean — smsOptIn === true AND phone valid
  hasPhoneNoOptIn, // boolean — phone valid but smsOptIn !== true
  smsUnknown,     // boolean — no user-doc resolved
  hasEmail,       // boolean — valid email format on player record
}) {
  // Determine each state. "unknown" is when we can't tell because the
  // parent has never created a Scrimly account (no user-doc to read).
  const pushState =
    pushUnknown ? 'unknown' :
    hasPush     ? 'on' : 'off';

  const smsState =
    smsUnknown        ? 'unknown' :
    hasSms            ? 'on' :
    hasPhoneNoOptIn   ? 'partial' :
    'off';

  const emailState = hasEmail ? 'on' : 'off';

  const pushTitle =
    pushUnknown ? 'App: unknown (parent has no Scrimly account yet)' :
    hasPush     ? 'App installed — push notifications enabled' :
    'App not installed — push notifications unavailable';

  const smsTitle =
    smsUnknown        ? 'SMS: unknown (parent has no Scrimly account yet)' :
    hasSms            ? 'SMS opted-in — will receive broadcasts via text' :
    hasPhoneNoOptIn   ? 'Has phone but not opted in to SMS' :
    'No phone on file — SMS unavailable';

  const emailTitle = hasEmail
    ? 'Valid email on file — will receive broadcasts via email'
    : 'No email on file — email unavailable';

  return (
    <div className="flex items-center gap-1 mt-1">
      <Badge icon="📱" label="App" state={pushState} title={pushTitle} />
      <Badge icon="💬" label="SMS" state={smsState} title={smsTitle} />
      <Badge icon="📧" label="Email" state={emailState} title={emailTitle} />
    </div>
  );
}
